import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { UserData, UserEntitlements, UserRole } from '@ddv/entitlements';
import { Client, UserPreferences } from '@ddv/models';
import { FuzzyDate } from '@hs/ui-core-date';
import { Fund, FundAvailability } from '@hs/ui-core-fund';
import { OptionToggleComponent } from '@hs/ui-core-inputs';

import { DefaultQueryParamsComponent } from '../default-query-params/default-query-params.component';
import { ExternalLinksComponent } from '../external-links/external-links.component';
import { LabeledRowComponent } from '../labeled-row/labeled-row.component';

@Component({
    selector: 'ddv-user-preferences-form',
    templateUrl: './user-preferences-form.component.html',
    styleUrls: ['./user-preferences-form.component.scss'],
    imports: [
        LabeledRowComponent,
        OptionToggleComponent,
        DefaultQueryParamsComponent,
        ExternalLinksComponent,
    ],
})
export class UserPreferencesFormComponent implements OnChanges {
    @Input() clientCode: string | undefined;
    @Input() isMultiClient = false;
    @Input() user: UserData | undefined;
    @Input() userEntitlements: UserEntitlements | undefined;
    @Input() userPreferences: UserPreferences | undefined;
    @Input() funds: Fund[] = [];
    @Input() clients: Client[] | undefined;
    @Input() fuzzyDates: FuzzyDate[] | undefined;

    @Output() userPreferencesChange = new EventEmitter<UserPreferences>();

    protected fullName: string | undefined;
    protected roleName: UserRole | undefined;
    protected autoDismissErrors = false;
    protected hasBothFundAvailability = false;
    protected preferHIForFundNames = false;
    protected startDate: string | undefined;
    protected endDate: string | undefined;
    protected clientCodes: string[] | undefined;
    protected fundCodes: string[] | undefined;
    protected showNQSAndCFBLinks = false;

    ngOnChanges(): void {
        this.fullName = this.user?.fullName;
        this.showNQSAndCFBLinks = !!this.user?.internal;
        this.roleName = this.userEntitlements?.user.roleName;
        this.autoDismissErrors = !!this.userPreferences?.autoDismissErrors;

        if (this.isMultiClient) {
            this.hasBothFundAvailability = false;
        } else {
            this.hasBothFundAvailability = this.funds?.some((fund) => fund.availability === FundAvailability.Both) ?? false;
        }
        this.preferHIForFundNames = this.userPreferences?.fundNamesSource === FundAvailability.HI;

        this.startDate = this.userPreferences?.startDate;
        this.endDate = this.userPreferences?.endDate;
        this.clientCodes = this.userPreferences?.clientsCodes;
        this.fundCodes = this.userPreferences?.fundsCodes;
    }

    protected updateAutoDismissErrorsPreference(): void {
        this.userPreferencesChange.emit(new UserPreferences({
            ...this.userPreferences,
            autoDismissErrors: this.autoDismissErrors,
        }));
    }

    protected updateFundNamesSourcePreference(): void {
        this.userPreferencesChange.emit(new UserPreferences({
            ...this.userPreferences,
            fundNamesSource: this.preferHIForFundNames ? FundAvailability.HI : FundAvailability.HSMain,
        }));
    }

    protected updateStartDatePreference(): void {
        this.userPreferencesChange.emit(new UserPreferences({
            ...this.userPreferences,
            startDate: this.startDate,
        }));
    }

    protected updateEndDatePreference(): void {
        this.userPreferencesChange.emit(new UserPreferences({
            ...this.userPreferences,
            endDate: this.endDate,
        }));
    }

    protected updateClientCodesPreference(): void {
        this.userPreferencesChange.emit(new UserPreferences({
            ...this.userPreferences,
            clientsCodes: this.clientCodes,
        }));
    }

    protected updateFundCodesPreference(): void {
        this.userPreferencesChange.emit(new UserPreferences({
            ...this.userPreferences,
            fundsCodes: this.fundCodes,
        }));
    }
}
